/**
 * Células editáveis para a tabela de Leads
 * Separadas para manter SRP e evitar re-renderizações desnecessárias
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Lead, FunnelStage, CrmUser } from '../../../types';
import Select from '../../../components/ui/Select';
import { AppIcon } from '../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';

/** Primeiro nome / local-part — mesmo critério da lista de tarefas. */
function shortAssigneeName(fullName: string): string {
  const name = fullName.trim();
  if (!name) return '';
  if (name.includes('@')) return name.split('@')[0]!;
  return name.split(/\s+/)[0]!;
}

interface LeadStageCellProps {
  lead: Lead;
  funnelStages: FunnelStage[];
  onUpdate: (lead: Lead, field: keyof Lead, value: any) => void;
}

/** Mesma escala de fallback do KanbanColumn. */
const STAGE_ACCENT_COLORS = [
  'var(--info-600)',
  'var(--warning-600)',
  'var(--brand-900)',
  'var(--success-600)',
] as const;

export const LeadStageCell: React.FC<LeadStageCellProps> = React.memo(
  ({ lead, funnelStages, onUpdate }) => {
    const handleStageChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        onUpdate(lead, 'stage', e.target.value);
      },
      [lead, onUpdate],
    );

    const value = lead.status === 'won' || lead.status === 'lost' ? lead.status : lead.stage;

    const options = useMemo(
      () => [
        ...funnelStages.map((stage) => ({ value: stage.id, label: stage.name })),
        { value: 'won', label: 'Ganho' },
        { value: 'lost', label: 'Perdido' },
      ],
      [funnelStages],
    );

    const stageIndex = funnelStages.findIndex((stage) => stage.id === value);
    const selectedStage = stageIndex >= 0 ? funnelStages[stageIndex] : undefined;
    const selectedLabel = options.find((option) => option.value === value)?.label ?? '';
    const isWon = value === 'won';
    const isLost = value === 'lost';

    const stageColor =
      selectedStage?.color ||
      (stageIndex >= 0
        ? STAGE_ACCENT_COLORS[stageIndex % STAGE_ACCENT_COLORS.length]
        : 'var(--brand-500)');

    const stageBadgeStyle: React.CSSProperties | undefined =
      !isWon && !isLost
        ? {
            backgroundColor: `color-mix(in srgb, ${stageColor} 16%, var(--surface-0))`,
            color: stageColor,
            borderColor: `color-mix(in srgb, ${stageColor} 32%, var(--surface-0))`,
          }
        : undefined;

    return (
      <div
        className="relative inline-flex max-w-full min-w-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={cn(
            'badge badge-sm pointer-events-none max-w-full gap-s1 border',
            'min-h-11 px-s3 md:min-h-0',
            isWon && 'badge-success',
            isLost && 'badge-danger',
          )}
          style={stageBadgeStyle}
          aria-hidden
        >
          <span className="min-w-0 truncate">{selectedLabel || '—'}</span>
          <AppIcon name="chevron-down" className="size-3 shrink-0 opacity-70" />
        </span>
        <Select
          id={`lead-stage-${lead.id}`}
          name={`lead-stage-${lead.id}`}
          autoComplete="off"
          value={value}
          onChange={handleStageChange}
          options={options}
          selectSize="sm"
          title={selectedLabel}
          aria-label={selectedLabel ? `Etapa do funil: ${selectedLabel}` : 'Etapa do funil'}
          containerClassName="absolute inset-0 m-0 min-w-0 w-full"
          className="h-full min-h-11 w-full cursor-pointer opacity-0 md:min-h-0"
        />
      </div>
    );
  },
);

LeadStageCell.displayName = 'LeadStageCell';

interface LeadValueCellProps {
  lead: Lead;
  onUpdate: (lead: Lead, field: keyof Lead, value: any) => void;
}

export const LeadValueCell: React.FC<LeadValueCellProps> = React.memo(({ lead, onUpdate }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const display = draft !== null ? draft : String(lead.value ?? 0);

  const commit = () => {
    if (draft === null) return;
    const normalized = draft.replace(/\s/g, '').replace(',', '.');
    const next = parseFloat(normalized);
    const parsed = Number.isFinite(next) ? next : 0;
    if (Math.abs(parsed - (lead.value ?? 0)) > 0.009) {
      onUpdate(lead, 'value', parsed);
    }
    setDraft(null);
  };

  return (
    <div className="inline-flex max-w-full items-center gap-s1" onClick={(e) => e.stopPropagation()}>
      <label className="sr-only" htmlFor={`lead-value-${lead.id}`}>
        Valor da negociação
      </label>
      <span className="shrink-0 text-xs font-normal text-muted" aria-hidden>
        R$
      </span>
      <input
        id={`lead-value-${lead.id}`}
        name={`lead-value-${lead.id}`}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        size={10}
        value={display}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(null);
            e.currentTarget.blur();
          }
        }}
        className={cn(
          'min-h-11 w-[10ch] max-w-full border-0 bg-transparent p-0 md:min-h-0',
          'font-mono text-sm font-normal tabular-nums tracking-tight text-muted',
          'outline-none focus-visible:ring-0',
        )}
      />
    </div>
  );
});

LeadValueCell.displayName = 'LeadValueCell';

interface LeadAgentCellProps {
  lead: Lead;
  crmUsers: CrmUser[];
  onUpdate: (lead: Lead, field: keyof Lead, value: any) => void;
}

export const LeadAgentCell: React.FC<LeadAgentCellProps> = React.memo(
  ({ lead, crmUsers, onUpdate }) => {
    const handleAgentChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        onUpdate(lead, 'agent', e.target.value || null);
      },
      [lead, onUpdate],
    );

    const options = useMemo(
      () => [
        { value: '', label: 'Ninguém' },
        ...crmUsers.map((user) => ({ value: user.id, label: user.name })),
      ],
      [crmUsers],
    );

    const agentId = lead.agent || '';
    const fullName = options.find((option) => option.value === agentId)?.label ?? '';
    const label = agentId ? shortAssigneeName(fullName) || 'Sem nome' : null;

    return (
      <div
        className="relative inline-flex min-h-11 max-w-full min-w-0 items-center md:min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        {label ? (
          <div className="pointer-events-none flex min-w-0 items-center gap-s2" aria-hidden>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-avatar-chart-1-bg text-2xs font-bold text-avatar-chart-1-icon">
              {label[0]?.toUpperCase() ?? '?'}
            </span>
            <span className="truncate text-sm font-medium text-secondary">{label}</span>
          </div>
        ) : (
          <span className="pointer-events-none text-sm italic text-muted" aria-hidden>
            Sem responsável
          </span>
        )}
        <Select
          id={`lead-agent-${lead.id}`}
          name={`lead-agent-${lead.id}`}
          autoComplete="off"
          value={agentId}
          onChange={handleAgentChange}
          options={options}
          selectSize="sm"
          aria-label={label ? `Atendente: ${label}` : 'Atribuir atendente'}
          containerClassName="absolute inset-0 m-0 min-w-0 w-full"
          className="h-full min-h-11 w-full cursor-pointer opacity-0 md:min-h-0"
        />
      </div>
    );
  },
);

LeadAgentCell.displayName = 'LeadAgentCell';
